// Phone voice input: record in the browser -> POST /api/ai?transcribe=1 -> text.
// The phone's own speech recognition (browserListen) is only a fallback when the route fails.

const MIMES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg']   // iPhone records mp4 (AAC)

export const canRecord = () => !!(typeof navigator !== 'undefined' && navigator.mediaDevices && navigator.mediaDevices.getUserMedia && typeof MediaRecorder !== 'undefined')
export const canBrowserListen = () => typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition)

// Start recording. Returns { stop(), done, heard() } - done resolves with the audio Blob.
// maxMs: hard stop. silenceMs + audioCtx: stop once the speaker has been quiet that long (short command clips).
// noSpeechMs: give up if nothing is heard at all.
export async function startRecording({ maxMs = 60000, silenceMs = 0, noSpeechMs = 0, audioCtx = null } = {}) {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
  const mime = MIMES.find(m => MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) || ''
  const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined)
  const chunks = []
  let timer = null, poll = null, src = null, heard = false
  const cleanup = () => {
    clearTimeout(timer); clearInterval(poll)
    try { if (src) src.disconnect() } catch (e) {}
    stream.getTracks().forEach(t => t.stop())   // release the mic straight away (iPhone speaker volume)
  }
  const done = new Promise(resolve => {
    rec.ondataavailable = e => { if (e.data && e.data.size) chunks.push(e.data) }
    rec.onstop = () => { cleanup(); resolve(new Blob(chunks, { type: rec.mimeType || mime || 'audio/webm' })) }
  })
  const stop = () => { if (rec.state !== 'inactive') rec.stop(); return done }
  timer = setTimeout(stop, maxMs)
  if (silenceMs && audioCtx) {
    try {
      src = audioCtx.createMediaStreamSource(stream)
      const an = audioCtx.createAnalyser(); an.fftSize = 1024; src.connect(an)
      const buf = new Float32Array(an.fftSize), t0 = Date.now()
      let quietSince = t0
      poll = setInterval(() => {
        an.getFloatTimeDomainData(buf)
        let s = 0; for (let i = 0; i < buf.length; i++) s += buf[i] * buf[i]
        const loud = Math.sqrt(s / buf.length) > 0.02, now = Date.now()
        if (loud) { heard = true; quietSince = now }
        else if (heard && now - quietSince > silenceMs) stop()
        if (!heard && noSpeechMs && now - t0 > noSpeechMs) stop()
      }, 100)
    } catch (e) {}
  }
  rec.start()
  return { stop, done, heard: () => heard }   // heard() is only meaningful with silence detection on
}

const toBase64 = blob => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(',')[1]); r.onerror = rej; r.readAsDataURL(blob) })

// an empty / very short recording: the same words as the server's answer (api/_transcribe.js TOO_SHORT)
export const TOO_SHORT = 'Nothing heard - the recording was empty or too short. Tap the mic, speak, then tap again.'
export const MIN_BYTES = 1500   // smaller is a tap, not speech: not sent
// Blob -> text ('' = nothing heard). mode 'command' tunes it for checklist commands. Throws if the route fails.
export async function transcribe(blob, mode) {
  if (!blob || blob.size < MIN_BYTES) return ''
  const r = await fetch('/api/ai?transcribe=1', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ audio: await toBase64(blob), mime: blob.type, mode }) })
  const d = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(d.error || ('HTTP ' + r.status))
  return String(d.text || '').trim()
}

// Fallback: the phone's own speech recognition. Returns { stop(), done } - done resolves with the text.
export function browserListen({ lang = 'en-GB' } = {}) {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition
  if (!SR) return { stop() {}, done: Promise.reject(new Error('Voice input is not supported in this browser')) }
  const r = new SR(); r.lang = lang; r.interimResults = false; r.maxAlternatives = 1
  let text = ''
  const done = new Promise((resolve, reject) => {
    r.onresult = e => { text = Array.from(e.results).map(x => x[0].transcript).join(' ') }
    r.onerror = e => { if (e.error === 'no-speech' || e.error === 'aborted') resolve(''); else reject(new Error(e.error || 'speech error')) }
    r.onend = () => resolve(text)
  })
  r.start()
  return { stop() { try { r.stop() } catch (e) {} }, done }
}
